import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Settings, LogOut, Shield, LayoutDashboard, Menu, X } from "lucide-react";
import { useState } from "react";

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const isActive = (path) => location.pathname === path;

  const getInitials = (name) => {
    if (!name) return "U";
    return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const navLinkClass = (path) =>
    `text-sm font-medium transition-colors ${isActive(path) ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`;

  return (
    <header
      className="sticky top-0 z-50 border-b border-border/50 bg-white/60 backdrop-blur-xl"
      data-testid="main-header"
    >
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link
          to="/"
          className="font-heading text-xl font-black tracking-tighter text-foreground"
          data-testid="brand-logo"
        >
          Click Colony
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
          {user && (
            <Link to="/dashboard" className={navLinkClass("/dashboard")} data-testid="nav-dashboard">
              Dashboard
            </Link>
          )}
          <Link to="/faq" className={navLinkClass("/faq")} data-testid="nav-faq">
            FAQ
          </Link>
          <Link to="/contact" className={navLinkClass("/contact")} data-testid="nav-contact">
            Contact
          </Link>
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {!user ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/login")}
                data-testid="nav-login-btn"
              >
                Log in
              </Button>
              <Button
                size="sm"
                className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white transition-all hover:shadow-lg hover:shadow-emerald-600/20"
                onClick={() => navigate("/register")}
                data-testid="nav-register-btn"
              >
                Apply
              </Button>
            </>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="relative h-9 w-9 rounded-full"
                  data-testid="user-menu-trigger"
                >
                  <Avatar className="h-9 w-9">
                    <AvatarFallback className="bg-foreground text-background text-xs font-semibold">
                      {getInitials(user.name)}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user.name}</p>
                  <p className="text-xs text-muted-foreground">{user.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                  <LayoutDashboard className="mr-2 h-4 w-4" /> Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
                  <Settings className="mr-2 h-4 w-4" /> Settings
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                    <Shield className="mr-2 h-4 w-4" /> Admin Panel
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                  <LogOut className="mr-2 h-4 w-4" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Mobile menu button */}
        <Button
          variant="ghost"
          size="sm"
          className="md:hidden"
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="mobile-menu-btn"
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-border bg-white px-6 py-4 space-y-3" data-testid="mobile-menu">
          {user && (
            <Link
              to="/dashboard"
              className="block text-sm font-medium py-1"
              onClick={() => setMobileOpen(false)}
            >
              Dashboard
            </Link>
          )}
          <Link to="/faq" className="block text-sm font-medium py-1" onClick={() => setMobileOpen(false)}>
            FAQ
          </Link>
          <Link to="/contact" className="block text-sm font-medium py-1" onClick={() => setMobileOpen(false)}>
            Contact
          </Link>
          {!user ? (
            <div className="flex gap-2 pt-2">
              <Button variant="ghost" size="sm" onClick={() => { navigate("/login"); setMobileOpen(false); }}>
                Log in
              </Button>
              <Button size="sm" onClick={() => { navigate("/register"); setMobileOpen(false); }}>
                Apply
              </Button>
            </div>
          ) : (
            <>
              <Link to="/settings" className="block text-sm font-medium py-1" onClick={() => setMobileOpen(false)}>
                Settings
              </Link>
              {user.role === "admin" && (
                <Link to="/admin" className="block text-sm font-medium py-1" onClick={() => setMobileOpen(false)}>
                  Admin Panel
                </Link>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { handleLogout(); setMobileOpen(false); }}
                data-testid="mobile-logout-btn"
              >
                Log out
              </Button>
            </>
          )}
        </div>
      )}
    </header>
  );
}
